/**
 * Ficha de producto (sections/product.liquid, T-008).
 *
 * Datos por variante en <script type="application/json" data-product-data>: precios ya formateados
 * por Liquid, stock, specs y unidades de armado. Acá solo se elige la variante, se mueve la galería
 * y se agrega al carrito con la Ajax Cart API (+ bundled section rendering para el contador del header).
 *
 * Armado: cantidad = ceil(unidades_armado × cantidad) del producto "Armado" (docs/catalogo.md).
 * Contrato con el futuro drawer (T-013): evento `cart:items-added` en document y `open()` del <cart-drawer>.
 *
 * Packs por cantidad (T-041, ADR 0017, assets/packs.js en globalThis.Packs): los escalones salen del metafield custom.packs
 * (data.packs.tiers). Las tarjetas «Llevá N» (data-packs) y el selector de la compra sticky fijan la cantidad; la cantidad a
 * mano elige el escalón (7 → precio de 5). Precio grande = web por unidad del escalón, con el total, el ahorro en pesos y el
 * precio por transferencia del escalón debajo; los cobra el checkout con los descuentos automáticos del admin. El armado usa
 * su propia escala por unidades (ADR 0008).
 */

const EPS = 1e-9;

/** Formatea centavos con el formato de moneda de la tienda (shop.money_format). */
function formatMoney(cents, format) {
  const formats = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_with_period_and_space_separator: [2, ' ', '.'],
  };
  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    const [decimals, thousands, decimal] = formats[key] ?? formats.amount;
    const [int, frac] = (cents / 100).toFixed(decimals).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac && !/^0+$/.test(frac) ? `${grouped}${decimal}${frac}` : grouped;
  });
}

/** Inputs properties[Clave] de un form de compra → { Clave: valor } o null si no hay. */
function formProperties(form) {
  const out = {};
  for (const input of form?.querySelectorAll('input[name^="properties["]') ?? []) {
    const key = input.name.slice('properties['.length, -1);
    if (key && input.value) out[key] = input.value;
  }
  return Object.keys(out).length ? out : null;
}

function fill(template, values) {
  return Object.entries(values).reduce((text, [key, value]) => text.replaceAll(`[${key}]`, value), template);
}

class ProductPage extends HTMLElement {
  connectedCallback() {
    if (this.data) return;
    const script = this.querySelector('[data-product-data]');
    if (!script) return;
    this.data = JSON.parse(script.textContent);
    this.strings = this.data.strings;
    this.variant = this.data.variants.find((v) => v.id === this.data.selected) ?? this.data.variants[0];

    this.picker = this.querySelector('[data-variant-picker]');
    this.form = this.querySelector('[data-product-form]');
    this.qty = this.querySelector('[data-qty]');
    this.addButton = this.querySelector('[data-add]');
    this.status = this.querySelector('[data-status]');
    this.armadoBox = this.querySelector('[data-armado]');
    this.armadoCheck = this.querySelector('[data-armado-check]');
    this.packs = this.querySelector('[data-packs]');
    this.stickyPacks = this.querySelector('[data-sticky-packs]');
    const Packs = globalThis.Packs;
    this.tiers = Packs ? Packs.parsePacks(this.data.packs?.tiers) : [];
    this.armadoScale = Packs ? Packs.parseScale(this.data.packs?.assemblyScale) : [];

    this.picker?.addEventListener('change', () => this.onOptionChange());
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.qty?.addEventListener('input', () => { this.syncPacks(); this.renderArmado(); this.syncSticky(); });
    // change (mouse y flechas del teclado) y también click: tocar el pack ya marcado (cantidad 7 con «Llevá 5» marcado) no
    // dispara change y igual fija la cantidad del pack.
    const pickPack = (event) => {
      const pack = event.target.closest('[data-pack]');
      if (!pack || pack.disabled || !pack.checked || !this.qty) return;
      this.qty.value = pack.value;
      this.syncPacks();
      this.renderArmado();
      this.syncSticky();
    };
    for (const group of [this.packs, this.stickyPacks]) {
      group?.addEventListener('change', pickPack);
      group?.addEventListener('click', pickPack);
    }
    this.setupGallery();
    this.renderPacks();
    this.renderArmado();
    this.syncSticky();
  }

  /** Cantidad elegida (entero ≥ 1). */
  quantity() {
    return Math.max(1, Math.floor(Number(this.qty?.value) || 1));
  }

  /* ---------- Packs ---------- */

  /** Precio de qty unidades de la variante actual con el escalón de custom.packs, o null sin packs. */
  packPrice(qty = this.quantity()) {
    const Packs = globalThis.Packs;
    if (!Packs || !this.tiers.length || !this.variant) return null;
    return Packs.packPrice(this.tiers, this.variant.cents, qty);
  }

  /** Precio por unidad, ahorro y stock de cada tarjeta para la variante actual (y stock del selector sticky). */
  renderPacks() {
    if (!this.variant || !this.tiers.length) return;
    const v = this.variant;
    for (const pack of this.querySelectorAll('[data-packs] [data-pack], [data-sticky-packs] [data-pack]')) {
      const qty = Number(pack.value) || 1;
      const noStock = !v.available || (typeof v.inventory === 'number' && v.inventory < qty);
      pack.disabled = noStock;
      const card = pack.closest('.packs__card');
      if (!card) continue;
      const price = this.packPrice(qty);
      card.querySelector('[data-pack-unit]').textContent = fill(this.strings.pack_unit, { price: this.money(price.unit) });
      card.querySelector('[data-pack-save]').textContent = price.save > 0 ? fill(this.strings.pack_save, { amount: this.money(price.save) }) : '';
      const stock = card.querySelector('[data-pack-stock]');
      stock.hidden = !noStock;
      stock.textContent = noStock ? fill(this.strings.pack_no_stock, { count: qty }) : '';
    }
    this.syncPacks();
  }

  /** La tarjeta marcada (ficha y sticky) es la del escalón de la cantidad (7 → «Llevá 5»), y el precio la sigue. */
  syncPacks() {
    const price = this.packPrice();
    if (!price) return;
    for (const pack of this.querySelectorAll('[data-packs] [data-pack], [data-sticky-packs] [data-pack]')) pack.checked = Number(pack.value) === price.min;
    this.renderPackPrice(price);
  }

  /** Precio grande (web por unidad del escalón), «c/u», total, ahorro en pesos y línea por transferencia del escalón. */
  renderPackPrice(price) {
    const qty = this.quantity();
    const current = this.querySelector('[data-price]');
    if (current) current.textContent = this.money(price.unit);
    const unit = this.querySelector('[data-price-unit]');
    if (unit) unit.hidden = qty <= 1;
    const total = this.querySelector('[data-pack-total]');
    if (total) {
      total.hidden = qty <= 1;
      total.textContent = qty > 1 ? fill(this.strings.pack_total, { count: qty, amount: this.money(price.total) }) : '';
    }
    const saving = this.querySelector('[data-pack-saving]');
    if (saving) {
      saving.hidden = price.save <= 0;
      saving.textContent = price.save > 0 ? fill(this.strings.pack_saving, { count: qty, amount: this.money(price.save) }) : '';
    }
    const transfer = this.querySelector('.product__price [data-transfer]');
    if (transfer) {
      transfer.hidden = price.transferUnit === null;
      if (price.transferUnit !== null) {
        const [before, after = ''] = (qty > 1 ? this.strings.transfer_line_unit : this.strings.transfer_line).split('[price]');
        const amount = document.createElement('span');
        amount.className = 'price-transfer__amount tabular';
        amount.dataset.transferPrice = '';
        amount.textContent = this.money(price.transferUnit);
        transfer.replaceChildren(before, amount, after);
      }
    }
    this.updateTransferWa(price);
  }

  /** «Pagar por transferencia» (T-045, ADR 0018): link de WhatsApp con producto, variante, cantidad, transferencia del escalón por
   * unidad, total y link de la ficha (Packs.transferOrder / transferText); en la compra sticky, además, el total en el texto.
   * T-049: etiqueta «N% OFF» del escalón (Packs.transferPercent). */
  updateTransferWa(price) {
    const links = this.querySelectorAll('[data-transfer-wa]');
    const Packs = globalThis.Packs;
    if (!links.length || !Packs || !this.variant || !this.data.whatsapp) return;
    const v = this.variant;
    const many = this.data.variants.length > 1;
    const url = new URL(this.data.productUrl);
    if (many) url.searchParams.set('variant', v.id);
    const order = Packs.transferOrder([{ title: many ? `${this.data.productTitle} (${v.title})` : this.data.productTitle, qty: this.quantity(), packs: this.data.packs?.tiers, price: v.cents }]);
    const s = this.strings;
    const { text, total } = Packs.transferText({ order, url: url.toString() }, { intro: s.wa_intro_product, line: s.wa_line, total: s.wa_total, outro: s.wa_outro }, this.data.moneyWithCurrency);
    const href = Packs.whatsappUrl(this.data.whatsapp, text);
    for (const link of links) {
      link.hidden = price.transferUnit === null;
      link.href = href;
      // Compra sticky: solo el monto («Transferencia: $X»), con el mismo formato que el precio de la fila 1.
      const amount = link.querySelector('[data-transfer-wa-amount]');
      if (amount) amount.textContent = this.money(total);
      // T-049: «N% OFF» del escalón elegido (web vs transferencia por unidad); sin descuento real, sin etiqueta.
      const badge = link.querySelector('[data-transfer-pct]');
      if (badge) {
        const pct = Packs.transferPercent(price.unit, price.transferUnit);
        badge.hidden = pct < 1;
        badge.textContent = pct < 1 ? '' : s.pct_off.replace('[pct]', pct);
      }
    }
  }

  /** Compra sticky (mobile): total del pack (escalón de custom.packs) y cantidad. */
  syncSticky() {
    const price = this.querySelector('[data-sticky-price]');
    if (!price || !this.variant) return;
    const qty = this.quantity();
    const pack = this.packPrice(qty);
    price.textContent = qty > 1 ? this.money(pack ? pack.total : this.variant.cents * qty) : pack ? this.money(pack.unit) : this.variant.price;
    const label = this.querySelector('[data-sticky-qty]');
    if (label) {
      label.hidden = qty <= 1;
      label.textContent = qty > 1 ? fill(this.strings.unidades_varias, { count: qty }) : '';
    }
  }

  money(cents) {
    return formatMoney(cents, this.data.moneyFormat);
  }

  /* ---------- Variantes ---------- */

  onOptionChange() {
    const selected = [...this.picker.querySelectorAll('fieldset')].map(
      (fieldset) => fieldset.querySelector('input:checked')?.value,
    );
    selected.forEach((value, index) => {
      const label = this.querySelector(`[data-option-label="${index}"]`);
      if (label) label.textContent = value ?? '';
    });
    const variant = this.data.variants.find((v) => v.options.every((option, i) => option === selected[i]));
    this.status.textContent = '';
    if (!variant) {
      this.variant = null;
      this.setButton(false, this.strings.no_disponible);
      this.status.textContent = this.strings.combinacion_no_disponible;
      return;
    }
    this.variant = variant;
    this.renderVariant();
  }

  renderVariant() {
    const v = this.variant;
    const idInput = this.querySelector('[data-variant-id]');
    if (idInput) idInput.value = v.id;

    const price = this.querySelector('[data-price]');
    if (price) price.textContent = v.price; // con packs, renderPacks lo pasa al escalón de la cantidad
    const compareWrap = this.querySelector('[data-compare-wrap]');
    if (compareWrap) {
      compareWrap.hidden = !v.compare;
      compareWrap.querySelector('[data-compare]').textContent = v.compare;
    }

    const shipping = this.querySelector('[data-shipping-line]');
    if (shipping && typeof v.cents === 'number') {
      const freeFrom = Number(shipping.dataset.freeFrom) || 0;
      const text = shipping.querySelector('[data-shipping-text]');
      if (freeFrom > 0 && v.cents >= freeFrom) text.textContent = shipping.dataset.free;
      else if (shipping.dataset.fromPrice) text.textContent = shipping.dataset.from.replace('[price]', shipping.dataset.fromPrice);
    }

    const badge = this.querySelector('[data-badge]');
    if (badge) {
      badge.textContent = v.badge;
      badge.hidden = !v.badge;
    }
    const build = this.querySelector('build-view');
    if (build && typeof build.setLoad === 'function') build.setLoad(v.loadLabel || '');
    if (build && typeof build.setDims === 'function') build.setDims(v.dims || []);
    // Variante de material del GLB (góndolas Negro / Blanco): mismo nombre que la variante de Shopify; sin recargar el modelo.
    if (build && typeof build.setVariant === 'function' && build.dataset.posters) build.setVariant(v.variant3d || '');

    const stock = this.querySelector('[data-stock]');
    if (stock) {
      stock.dataset.state = v.stock;
      stock.textContent = v.stockText;
      stock.hidden = !v.stockText;
    }

    for (const row of this.querySelectorAll('[data-spec]')) {
      const value = v.specs[row.dataset.spec] ?? '';
      row.querySelector('td, dd').textContent = value;
      row.hidden = !value;
    }

    this.setButton(v.available, v.available ? this.strings.agregar : this.strings.agotado);
    this.updateWhatsapp();
    // «¿Cuántas necesitás?»: el color va a la calculadora de góndolas.
    const calcColor = this.querySelector('[data-calc-color]');
    if (calcColor) calcColor.value = this.data.variants.length > 1 && v.variant3d ? v.variant3d : '';
    // «Comparar en 3D» con el color actual (solo con variantes reales).
    const compareLink = this.querySelector('[data-compare-link]');
    if (compareLink?.dataset.compareBase) compareLink.href = this.data.variants.length > 1 && v.variant3d ? `${compareLink.dataset.compareBase}:${encodeURIComponent(v.variant3d)}` : compareLink.dataset.compareBase;
    this.renderPacks();
    this.renderArmado();
    this.syncSticky();
    // La foto de la variante solo si el carril está en una foto: en la slide 3D el color cambia en el modelo (variant-name) y
    // la galería se queda en el 3D.
    if (v.media && this.slides?.[this.active]?.dataset.mediaId !== '3d') this.showMedia(v.media);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    window.history.replaceState(window.history.state, '', url);
  }

  /** Estado de los botones de compra: «Comprar ahora» (y «Comprar» de la sticky) dice «Agotado» sin stock; «Agregar al carrito»
   * conserva su texto y se deshabilita; el «Agregar» de la sticky es un ícono (no cambia el texto). */
  setButton(enabled, text) {
    if (!this.addButton) return;
    this.addButton.disabled = !enabled;
    this.addButton.textContent = enabled ? text : this.strings.agregar;
    for (const buy of this.querySelectorAll('[data-buy-now]')) {
      buy.disabled = !enabled;
      buy.textContent = enabled ? buy.dataset.label : this.strings.agotado;
    }
    const sticky = this.querySelector('[data-sticky-add]');
    if (sticky) sticky.disabled = !enabled;
  }

  updateWhatsapp() {
    const link = this.querySelector('[data-whatsapp]');
    if (!link || !this.data.whatsapp) return;
    const v = this.variant;
    const producto = this.data.variants.length > 1 ? `${this.data.productTitle} – ${v.title}` : this.data.productTitle;
    const url = new URL(this.data.productUrl);
    if (this.data.variants.length > 1) url.searchParams.set('variant', v.id);
    const text = fill(this.strings.wa_texto, { producto, url: url.toString() });
    link.href = `https://wa.me/${this.data.whatsapp}?text=${encodeURIComponent(text)}`;
  }

  /* ---------- Armado ---------- */

  armadoQty() {
    if (!this.data.armado || !this.variant) return 0;
    return Math.ceil(this.variant.unidades * this.quantity() - EPS);
  }

  /** «N unidades de armado × $X» con la escala de armado (estimado; tachado cuando hay descuento). */
  renderArmado() {
    if (!this.armadoBox || !this.data.armado) return;
    const n = this.armadoQty();
    this.armadoBox.hidden = n < 1;
    if (n < 1) return;
    const Packs = globalThis.Packs;
    const tarifa = this.data.armado.price;
    const save = Packs ? Packs.savings(tarifa, n, this.armadoScale) : 0;
    const unit = Packs ? Packs.roundMoney(Packs.unitPrice(tarifa, n, this.armadoScale)) : tarifa;
    this.armadoBox.querySelector('[data-armado-price]').textContent = `+ ${this.money(Packs ? Packs.roundMoney(n * tarifa - save) : n * tarifa)}`;
    const template = n === 1 ? this.strings.armado_una : this.strings.armado_varias;
    this.armadoBox.querySelector('[data-armado-detail]').textContent = fill(template, { count: n, tarifa: this.money(unit) });
    const regular = this.armadoBox.querySelector('[data-armado-regular]');
    if (regular) regular.hidden = save <= 0;
  }

  /* ---------- Carrito ---------- */

  headerSectionId() {
    const section = document.querySelector('.site-header__cart')?.closest('.shopify-section');
    return section?.id.replace(/^shopify-section-/, '') || null;
  }

  /** Ítems del form: variante con la cantidad (o el pack), propiedades de línea (preventa) y el armado si está marcado. */
  cartItems() {
    const quantity = this.quantity();
    if (this.qty) this.qty.value = quantity;
    const items = [{ id: this.variant.id, quantity }];
    const properties = formProperties(this.form);
    if (properties) items[0].properties = properties;
    if (this.armadoCheck?.checked && this.armadoQty() > 0) items.push({ id: this.data.armado.id, quantity: this.armadoQty() });
    return items;
  }

  /** «Comprar ahora» (T-046): /cart/add.js sin vaciar el carrito y redirección a /checkout; los descuentos de pack los aplica el
   * checkout. Mientras carga, los botones de compra quedan deshabilitados y el que se tocó con aria-busy; si falla (sin stock,
   * error), el mismo mensaje que «Agregar» y no se redirige. */
  async buyNow(button) {
    const root = window.Shopify?.routes?.root ?? '/';
    const buttons = [...this.querySelectorAll('[data-buy-now], [data-add], [data-sticky-add]')];
    buttons.forEach((b) => { b.disabled = true; });
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.strings.yendo_pago;
    this.status.textContent = '';
    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: this.cartItems() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || this.strings.error);
      window.location.assign(`${root}checkout`);
    } catch (error) {
      this.status.textContent = error.message || this.strings.error;
      button.removeAttribute('aria-busy');
      this.setButton(this.variant.available, this.variant.available ? this.strings.agregar : this.strings.agotado);
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (!this.variant?.available) return;
    // T-046: «Comprar ahora» / «Comprar» (data-buy-now) agrega lo mismo y va directo al pago.
    if (event.submitter?.hasAttribute('data-buy-now')) return this.buyNow(event.submitter);
    // Propiedades de línea del form (preventa: «Entrega: preventa, llega el …», snippets/preventa-property.liquid) y armado.
    const items = this.cartItems();
    const conArmado = items.length > 1;

    const sectionId = this.headerSectionId();
    const payload = { items };
    if (sectionId) {
      payload.sections = [sectionId];
      payload.sections_url = window.location.pathname;
    }

    this.addButton.disabled = true;
    const sticky = this.querySelector('[data-sticky-add]');
    if (sticky) sticky.disabled = true;
    for (const buy of this.querySelectorAll('[data-buy-now]')) buy.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.textContent = this.strings.agregando;
    this.status.textContent = '';
    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || this.strings.error);

      document.dispatchEvent(new CustomEvent('cart:fly', { detail: { image: this.querySelector('[data-gallery-track] img') } }));
      if (sectionId && body.sections?.[sectionId]) this.updateHeaderCart(body.sections[sectionId]);
      document.dispatchEvent(
        new CustomEvent('cart:items-added', { detail: { source: 'product', items: body.items ?? items } }),
      );
      const message = conArmado ? this.strings.agregado_con_armado : this.strings.agregado;
      const drawer = document.querySelector('cart-drawer');
      if (typeof drawer?.open === 'function') {
        drawer.open();
        this.status.textContent = message;
      } else {
        const link = document.createElement('a');
        link.href = window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart';
        link.textContent = this.strings.ver_carrito;
        this.status.replaceChildren(`${message} `, link);
      }
    } catch (error) {
      this.status.textContent = error.message || this.strings.error;
    } finally {
      this.addButton.removeAttribute('aria-busy');
      this.setButton(this.variant.available, this.variant.available ? this.strings.agregar : this.strings.agotado);
    }
  }

  updateHeaderCart(html) {
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.site-header__cart');
    const current = document.querySelector('.site-header__cart');
    if (fresh && current) current.replaceChildren(...fresh.childNodes);
  }

  /* ---------- Galería ---------- */

  /** Carril con scroll-snap: miniaturas, flechas anterior/siguiente y teclado (flechas con el foco en el carril). En la
   * diapositiva 3D arrastrar gira el modelo (model-viewer, touch-action pan-y); el cambio de slide va por flechas, miniaturas
   * y swipe fuera del canvas. */
  setupGallery() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.children];
    this.thumbs = [...this.querySelectorAll('[data-thumb]')];
    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.prev?.addEventListener('click', () => this.goTo(this.active - 1));
    this.next?.addEventListener('click', () => this.goTo(this.active + 1));
    this.track.addEventListener('keydown', (event) => {
      // Solo con el foco en el carril mismo: las flechas dentro del switch Armada | Piezas son del radiogroup.
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.active + 1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.active - 1); }
    });
    let frame;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.markActive(Math.round(this.track.scrollLeft / this.track.clientWidth)));
      },
      { passive: true },
    );
    this.markActive(0);
  }

  goTo(index) {
    if (!this.slides?.[index]) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: reduce ? 'auto' : 'smooth' });
    this.markActive(index);
  }

  /** La miniatura activa queda a la vista dentro de su columna o fila (sin mover la página). */
  revealThumb(thumb) {
    const list = thumb.closest('.product-gallery__thumbs');
    if (!list) return;
    const item = thumb.parentElement;
    const top = item.offsetTop - list.offsetTop;
    const left = item.offsetLeft - list.offsetLeft;
    const options = { behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' };
    if (list.scrollHeight > list.clientHeight) {
      if (top < list.scrollTop) list.scrollTo({ top, ...options });
      else if (top + item.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTo({ top: top + item.offsetHeight - list.clientHeight, ...options });
    } else if (list.scrollWidth > list.clientWidth) {
      if (left < list.scrollLeft) list.scrollTo({ left, ...options });
      else if (left + item.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollTo({ left: left + item.offsetWidth - list.clientWidth, ...options });
    }
  }

  showMedia(mediaId) {
    const index = this.slides?.findIndex((slide) => Number(slide.dataset.mediaId) === mediaId) ?? -1;
    if (index >= 0) this.goTo(index);
  }

  markActive(index) {
    const clamped = Math.min(Math.max(index, 0), (this.slides?.length || 1) - 1);
    if (clamped === this.active && this.thumbs?.[clamped]?.getAttribute('aria-current') === 'true') return;
    this.active = clamped;
    this.thumbs?.forEach((thumb, i) => {
      if (i === clamped) { thumb.setAttribute('aria-current', 'true'); this.revealThumb(thumb); }
      else thumb.removeAttribute('aria-current');
    });
    if (this.prev) this.prev.disabled = clamped <= 0;
    if (this.next) this.next.disabled = clamped >= (this.slides?.length || 1) - 1;
  }
}

if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);
